import { MUHURTHAM_GOLD, MuhurthamMark } from '@/components/CalendarMarks'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isSunday,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { useMemo, useState } from 'react'
import { useHallSlotBoard, useMuhurthamDates } from '@/api/hooks'
import type { Hall, SlotStatus } from '@/api/types'
import { EmptyState, formatMoney, formatSlotHours, InlineSpinner } from '@/components/common'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * The month grid runs at roughly 60% of the height it used to: a day cell was
 * 92 pixels and is now 55, which brings a six-week month back inside one screen
 * without scrolling.
 *
 * <p>Text is not scaled with it. A slot abbreviation at 60% would be five
 * pixels and unreadable, so the markers keep a legible floor and the cell
 * tightens around them instead.</p>
 */
const DAY_CELL_HEIGHT = 64
const DAY_GRID_GAP = 3
const SLOT_MARKER_HEIGHT = 9

/**
 * Seven square days, the gaps between them and the card's own padding - so the
 * card ends where the calendar does instead of trailing empty space beside it.
 */
const CALENDAR_WIDTH = 7 * DAY_CELL_HEIGHT + 6 * DAY_GRID_GAP + 32

/** The slot names shortened to fit a day of a month grid. */
const SHORT_NAMES: Record<string, string> = {
  BREAKFAST: 'B',
  LUNCH: 'L',
  HI_TEA: 'HT',
  DINNER: 'D',
  FULL_DAY: 'FD',
  // Both banquet slots start "SL", so the fallback would render them alike.
  SLOT_1: 'S1',
  SLOT_2: 'S2',
}

function shortName(slot: SlotStatus) {
  return SHORT_NAMES[slot.slotCode] ?? slot.slotCode.slice(0, 2)
}

/**
 * A hall's month, slot by slot.
 *
 * <p>The timeline beside this shows the same bookings as bars across a fortnight,
 * which is the right way to see how the buffers overlap. It is the wrong way to
 * answer the question the phone actually asks: is the evening free on the
 * twelfth. That wants a month at a glance with every slot of every day on it,
 * which is what this draws.</p>
 */
export function HallSlotCalendar({ halls }: { halls: Hall[] }) {
  const [hallId, setHallId] = useState('')
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [selected, setSelected] = useState<Date | null>(new Date())

  const hall = halls.find((candidate) => candidate.id === hallId) ?? halls[0]
  const activeHallId = hallId || hall?.id

  // Whole weeks, so the grid always starts on a Monday.
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 })
  const from = format(gridStart, 'yyyy-MM-dd')
  const to = format(gridEnd, 'yyyy-MM-dd')

  const { data: board, isLoading } = useHallSlotBoard(activeHallId, from, to)
  const { data: muhurthams } = useMuhurthamDates(from, to)

  const days = useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart, gridEnd])

  const slotsByDay = useMemo(() => {
    const map = new Map<string, SlotStatus[]>()
    for (const day of board ?? []) map.set(day.date.slice(0, 10), day.slots)
    return map
  }, [board])

  const muhurthamByDay = useMemo(
    () => new Set((muhurthams ?? []).map((entry) => entry.date.slice(0, 10))),
    [muhurthams],
  )

  const key = (day: Date) => format(day, 'yyyy-MM-dd')
  const selectedSlots = selected ? slotsByDay.get(key(selected)) ?? [] : []

  /** How much of the month is still sellable, which is the number that matters. */
  const openThisMonth = useMemo(() => {
    let open = 0
    let total = 0
    for (const day of board ?? []) {
      if (!isSameMonth(new Date(`${day.date.slice(0, 10)}T00:00:00`), month)) continue
      for (const slot of day.slots) {
        total++
        if (slot.available) open++
      }
    }
    return { open, total }
  }, [board, month])

  if (halls.length === 0) {
    return (
      <EmptyState
        message="No halls are visible for this outlet."
        hint="Check your outlet access, or set up a hall under Settings."
      />
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg="auto">
        <Card sx={{ width: { lg: CALENDAR_WIDTH } }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              alignItems={{ sm: 'center' }}
              sx={{ mb: 1.5 }}
            >
              <TextField
                select
                size="small"
                label="Hall"
                value={activeHallId ?? ''}
                onChange={(event) => setHallId(event.target.value)}
                sx={{ minWidth: 240 }}
              >
                {halls.map((candidate) => (
                  <MenuItem key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <Typography variant="subtitle1">{format(month, 'MMMM yyyy')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {openThisMonth.open} of {openThisMonth.total} slots still open
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.5} sx={{ ml: { sm: 'auto' } }}>
                <Tooltip title="This month">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setMonth(startOfMonth(new Date()))
                      setSelected(new Date())
                    }}
                  >
                    <TodayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setMonth((m) => addMonths(m, -1))}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setMonth((m) => addMonths(m, 1))}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
              <LegendChip taken={false} label="Open" />
              <LegendChip taken label="Taken" />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ color: MUHURTHAM_GOLD, display: 'flex', fontSize: 13 }}>
                  <MuhurthamMark />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Muhurtham
                </Typography>
              </Stack>
            </Stack>

            {isLoading ? <InlineSpinner /> : null}

            {/* Square days: the columns are held to the cell height rather than
                stretched across the card. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(7, ${DAY_CELL_HEIGHT}px)`,
                justifyContent: 'start',
                gap: `${DAY_GRID_GAP}px`,
              }}
            >
              {WEEKDAYS.map((label) => (
                <Typography
                  key={label}
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    pb: 0.25,
                    // Sunday in red, the way every wall calendar in the office
                    // already marks it. The eye finds the week's end without
                    // counting across from Monday.
                    color: label === 'Sun' ? 'error.main' : 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              ))}

              {days.map((day) => {
                const slots = slotsByDay.get(key(day)) ?? []
                const muhurtham = muhurthamByDay.has(key(day))
                const outside = !isSameMonth(day, month)
                const isSelected = selected != null && isSameDay(day, selected)
                const allTaken = slots.length > 0 && slots.every((slot) => !slot.available)

                return (
                  <Box
                    key={day.toISOString()}
                    onClick={() => setSelected(day)}
                    sx={{
                      minHeight: DAY_CELL_HEIGHT,
                      p: 0.375,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: (theme) =>
                        `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                      bgcolor: (theme) =>
                        isSelected
                          ? theme.palette.action.selected
                          : muhurtham
                            ? alpha(theme.palette.secondary.main, 0.14)
                            : theme.palette.background.paper,
                      borderLeft: (theme) =>
                        muhurtham ? `3px solid ${theme.palette.secondary.main}` : undefined,
                      opacity: outside ? 0.45 : 1,
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 15,
                          lineHeight: 1.2,
                          // Read at arm's length across a desk, so bold
                          // throughout and heavier again for today.
                          fontWeight: isToday(day) ? 800 : 600,
                          // Sunday wins over today: the red is what the day is,
                          // and today is already carrying the heavier weight.
                          color: isSunday(day)
                            ? 'error.main'
                            : isToday(day)
                              ? 'primary.main'
                              : 'text.primary',
                        }}
                      >
                        {format(day, 'd')}
                      </Typography>
                      <Stack direction="row" spacing={0.25} alignItems="center">
                        {muhurtham ? (
                          <Box
                            title="Muhurtham"
                            sx={{ color: MUHURTHAM_GOLD, display: 'flex', fontSize: 13 }}
                          >
                            <MuhurthamMark />
                          </Box>
                        ) : null}
                      </Stack>
                    </Stack>

                    {/* Every slot of the day, in order, whether it is open or gone. */}
                    <Stack spacing={0.2} sx={{ mt: 0.25 }}>
                      {slots.map((slot) => (
                        <Tooltip
                          key={slot.slotId}
                          title={
                            // The hours belong on both branches: the office still
                            // needs to know which window is gone, not just that one is.
                            slot.available
                              ? `${slot.name} ${formatSlotHours(slot.startTime, slot.endTime)} · ${formatMoney(slot.rentalCharge)}`
                              : `${slot.name} ${formatSlotHours(slot.startTime, slot.endTime)} · held by ${slot.heldBy ?? 'another booking'}`
                          }
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: SLOT_MARKER_HEIGHT,
                              borderRadius: 0.5,
                              fontSize: 9,
                              lineHeight: 1,
                              letterSpacing: 0.2,
                              bgcolor: (theme) =>
                                slot.available
                                  ? alpha(theme.palette.success.main, 0.16)
                                  : alpha(theme.palette.error.main, 0.75),
                              color: (theme) =>
                                slot.available ? theme.palette.text.secondary : theme.palette.common.white,
                            }}
                          >
                            {shortName(slot)}
                          </Box>
                        </Tooltip>
                      ))}
                    </Stack>

                    {allTaken ? (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: 9, color: 'error.main', display: 'block', mt: 0.125 }}
                      >
                        full
                      </Typography>
                    ) : null}
                  </Box>
                )
              })}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* ------------------------------------------------------- the chosen day */}
      <Grid item xs={12} lg>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                {selected ? format(selected, 'EEEE d MMMM') : 'Pick a day'}
              </Typography>
              {selected && muhurthamByDay.has(key(selected)) ? (
                <Chip size="small" color="secondary" label="Muhurtham" />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {hall?.name}
            </Typography>
            <Divider sx={{ my: 1 }} />

            {selectedSlots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This hall has no slots set up.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slot</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell align="right">Rental</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSlots.map((slot) => (
                    <TableRow key={slot.slotId} hover>
                      <TableCell>{slot.name}</TableCell>
                      <TableCell>{formatSlotHours(slot.startTime, slot.endTime)}</TableCell>
                      <TableCell align="right">{formatMoney(slot.rentalCharge)}</TableCell>
                      <TableCell>
                        {slot.available ? (
                          <Chip size="small" variant="outlined" color="success" label="Open" />
                        ) : (
                          <Tooltip title={slot.heldBy ?? ''}>
                            <Chip size="small" color="error" label={slot.heldBy ?? 'Taken'} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

function LegendChip({ taken, label }: { taken: boolean; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box
        sx={{
          width: 16,
          height: 10,
          borderRadius: 0.5,
          bgcolor: (theme) =>
            taken ? alpha(theme.palette.error.main, 0.75) : alpha(theme.palette.success.main, 0.16),
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}
